import React, { useEffect, useState } from "react";
import {
  Box, Card, CardContent, CardHeader,
  Typography, Divider, Button,
  IconButton, Table, TableBody,
  TableCell, TableContainer, TableHead,
  TableRow, Paper, Select, MenuItem,
  Chip, Stack
} from "@mui/material";
import { Add, Edit, Delete } from "@mui/icons-material";
import {
  collection, getDocs, addDoc,
  updateDoc, deleteDoc, doc
} from "firebase/firestore";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { db } from "../firebase";
import Swal from "sweetalert2";

const colors = {
  darkGreen: '#06362D',
  calPolyGreen: '#155332',
  vanilla: '#DEE2B1',
  gold: '#FFD500',
  goldenrod: '#D1A505'
};

function Roomtarrifs() {
  const auth = getAuth();

  const [userId, setUserId] = useState(null);
  const [propertyId, setPropertyId] = useState(null);

  const [rooms, setRooms] = useState([]);
  const [plans, setPlans] = useState([]);
  const [tariffs, setTariffs] = useState([]);

  const [form, setForm] = useState({
    roomId: "",
    ratePlanId: "",
    status: "active"
  });

  const [editId, setEditId] = useState(null);

  /* ================= AUTH ================= */
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setUserId(user.uid);
        const snap = await getDocs(collection(db, "authroles"));
        snap.forEach(d => {
          if (d.id === user.uid) {
            setPropertyId(d.data().propertyId);
          }
        });
      }
    });
    return () => unsub();
  }, []);

  /* ================= FETCH DATA ================= */
  useEffect(() => {
    if (userId && propertyId) fetchAll();
  }, [userId, propertyId]);

  const fetchAll = async () => {
    const roomSnap = await getDocs(collection(db, "rooms"));
    setRooms(
      roomSnap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(r => r.propertyId === propertyId)
    );

    const planSnap = await getDocs(collection(db, "rate_plans"));
    setPlans(
      planSnap.docs.map(d => ({ id: d.id, ...d.data() }))
    );

    const tariffSnap = await getDocs(collection(db, "room_tariffs"));
    setTariffs(
      tariffSnap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(t => t.propertyId === propertyId)
    );
  };

  /* ================= SAVE ================= */
  const saveTariff = async () => {
    if (!form.roomId || !form.ratePlanId) {
      return Swal.fire("Required", "Room & Rate Plan are required", "warning");
    }

    if (editId) {
      await updateDoc(doc(db, "room_tariffs", editId), form);
      Swal.fire("Updated", "Room plan updated", "success");
    } else {
      await addDoc(collection(db, "room_tariffs"), {
        ...form,
        userId,
        propertyId,
        createdAt: new Date()
      });
      Swal.fire("Added", "Room plan added", "success");
    }

    resetForm();
    fetchAll();
  };

  /* ================= EDIT ================= */
  const editTariff = (row) => {
    setForm({
      roomId: row.roomId,
      ratePlanId: row.ratePlanId,
      status: row.status
    });
    setEditId(row.id);
  };

  /* ================= DELETE ================= */
  const deleteTariff = async (id) => {
    const ok = await Swal.fire({
      title: "Delete mapping?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete"
    });

    if (ok.isConfirmed) {
      await deleteDoc(doc(db, "room_tariffs", id));
      Swal.fire("Deleted", "Mapping removed", "success");
      fetchAll();
    }
  };

  /* ================= HELPERS ================= */
  const resetForm = () => {
    setForm({ roomId: "", ratePlanId: "", status: "active" });
    setEditId(null);
  };

  /* ================= GROUP BY ROOM ================= */
  const grouped = rooms.map(room => ({
    ...room,
    plans: tariffs.filter(t => t.roomId === room.id)
  }));

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: colors.darkGreen, color: colors.vanilla, p: { xs: 2, md: 4 } }}>
      <Box component="main" sx={{ flexGrow: 1, maxWidth: "1400px", margin: "0 auto" }}>
        
        <Typography variant="h5" fontWeight={800} mb={3} sx={{ color: colors.vanilla }}>
          Room – Rate Plan Mapping
        </Typography>

        {/* ================= FORM ================= */}
        <Card sx={{ mb: 4, borderRadius: 3, border: `1px solid ${colors.goldenrod}`, bgcolor: colors.calPolyGreen, color: colors.vanilla, boxShadow: "0 4px 20px 0 rgba(0,0,0,0.3)" }}>
          <CardHeader title={editId ? "Edit Mapping" : "Add Room Plan"} sx={{ '& .MuiCardHeader-title': { color: colors.vanilla, fontWeight: 700 } }} />
          <Divider sx={{ borderColor: colors.goldenrod, opacity: 0.4 }} />
          <CardContent sx={{ p: 3 }}>
            <div className="container-fluid p-0">
              <div className="row">
                <div className="col-sm-4 mb-3">
                  <Select
                    fullWidth
                    size="small"
                    value={form.roomId}
                    onChange={(e) => setForm({ ...form, roomId: e.target.value })}
                    displayEmpty
                    sx={{ 
                      color: colors.vanilla, 
                      backgroundColor: colors.darkGreen,
                      borderRadius: "8px",
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.goldenrod }, 
                      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: colors.gold }, 
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: colors.gold },
                      '& .MuiSvgIcon-root': { color: colors.vanilla }
                    }}
                  >
                    <MenuItem value="">Select Room</MenuItem>
                    {rooms.map(r => (
                      <MenuItem key={r.id} value={r.id}>
                        {r.roomTypeName}
                      </MenuItem>
                    ))}
                  </Select>
                </div>

                <div className="col-sm-4 mb-3">
                  <Select
                    fullWidth
                    size="small"
                    value={form.ratePlanId}
                    onChange={(e) => setForm({ ...form, ratePlanId: e.target.value })}
                    displayEmpty
                    sx={{ 
                      color: colors.vanilla, 
                      backgroundColor: colors.darkGreen,
                      borderRadius: "8px",
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.goldenrod }, 
                      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: colors.gold }, 
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: colors.gold },
                      '& .MuiSvgIcon-root': { color: colors.vanilla }
                    }}
                  >
                    <MenuItem value="">Select Plan</MenuItem>
                    {plans.map(p => (
                      <MenuItem key={p.id} value={p.id}>
                        {p.planCode} - {p.planName}
                      </MenuItem>
                    ))}
                  </Select>
                </div>

                <div className="col-sm-2 mb-3">
                  <Select
                    fullWidth
                    size="small"
                    value={form.status}
                    onChange={(e) => setForm({ ...form, status: e.target.value })}
                    sx={{ 
                      color: colors.vanilla, 
                      backgroundColor: colors.darkGreen,
                      borderRadius: "8px",
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.goldenrod }, 
                      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: colors.gold }, 
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: colors.gold },
                      '& .MuiSvgIcon-root': { color: colors.vanilla }
                    }}
                  >
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                  </Select>
                </div>

                <div className="col-sm-2 mb-3">
                  <Button
                    fullWidth
                    size="small"
                    variant="contained"
                    startIcon={<Add />}
                    onClick={saveTariff}
                    sx={{ 
                      bgcolor: colors.goldenrod, 
                      color: colors.darkGreen, 
                      fontWeight: 700, 
                      textTransform: "none", 
                      borderRadius: "8px",
                      height: "38px",
                      '&:hover': { bgcolor: colors.gold } 
                    }}
                  >
                    {editId ? "Update" : "Add"}
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* ================= TABLE ================= */}
        <Card sx={{ borderRadius: 3, border: `1px solid ${colors.goldenrod}`, bgcolor: colors.calPolyGreen, color: colors.vanilla, boxShadow: "0 4px 20px 0 rgba(0,0,0,0.3)" }}>
          <CardHeader title="Room Plans" sx={{ '& .MuiCardHeader-title': { color: colors.vanilla, fontWeight: 700 } }} />
          <Divider sx={{ borderColor: colors.goldenrod, opacity: 0.4 }} />
          <TableContainer component={Paper} elevation={0} sx={{ borderRadius: "8px", border: `1px solid ${colors.goldenrod}`, backgroundColor: "transparent" }}>
            <Table size="small">
              <TableHead sx={{ bgcolor: colors.darkGreen }}>
                <TableRow>
                  <TableCell sx={{ color: colors.vanilla, fontWeight: 700, fontSize: '0.75rem' }}>#</TableCell>
                  <TableCell sx={{ color: colors.vanilla, fontWeight: 700, fontSize: '0.75rem' }}>Room</TableCell>
                  <TableCell sx={{ color: colors.vanilla, fontWeight: 700, fontSize: '0.75rem' }}>Plans</TableCell>
                  <TableCell sx={{ color: colors.vanilla, fontWeight: 700, fontSize: '0.75rem' }}>Status</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {grouped.map((room, idx) => (
                  <TableRow key={room.id} hover sx={{ '&:hover': { bgcolor: 'rgba(255,255,255,0.05)' } }}>
                    <TableCell sx={{ color: colors.vanilla, borderBottom: `1px solid rgba(222,226,177,0.1)` }}>{idx + 1}</TableCell>
                    <TableCell sx={{ color: colors.gold, fontWeight: 600, borderBottom: `1px solid rgba(222,226,177,0.1)` }}>
                      {room.roomTypeName}
                    </TableCell>

                    <TableCell sx={{ borderBottom: `1px solid rgba(222,226,177,0.1)` }}>
                      <Stack spacing={1} sx={{ py: 1 }}>
                        {room.plans.length ? room.plans.map(p => {
                          const planName =
                            plans.find(pl => pl.id === p.ratePlanId)?.planName || "-";
                          return (
                            <Paper
                              key={p.id}
                              elevation={0}
                              sx={{
                                p: 1,
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                borderRadius: 2,
                                bgcolor: colors.darkGreen,
                                border: `1px solid ${colors.goldenrod}`,
                                color: colors.vanilla
                              }}
                            >
                              <Typography variant="body2" sx={{ color: colors.vanilla }}>
                                {planName}
                              </Typography>
                              <Stack direction="row" spacing={0.5}>
                                <IconButton size="small" sx={{ color: colors.gold }} onClick={() => editTariff(p)}>
                                  <Edit fontSize="small" />
                                </IconButton>
                                <IconButton size="small" color="error" onClick={() => deleteTariff(p.id)}>
                                  <Delete fontSize="small" />
                                </IconButton>
                              </Stack>
                            </Paper>
                          );
                        }) : (
                          <Typography variant="body2" sx={{ color: colors.vanilla, opacity: 0.7 }}>No plans</Typography>
                        )}
                      </Stack>
                    </TableCell>

                    <TableCell sx={{ borderBottom: `1px solid rgba(222,226,177,0.1)` }}>
                      {room.plans.length ? (
                        <Chip
                          label={room.plans[0].status}
                          color={room.plans[0].status === "active" ? "success" : "default"}
                          size="small"
                        />
                      ) : (
                        <Chip label="N/A" size="small" />
                      )}
                    </TableCell>
                  </TableRow>
                ))}

                {!grouped.length && (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 4, color: colors.vanilla, opacity: 0.7 }}>
                      No rooms found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      </Box>
    </Box>
  );
}

export default Roomtarrifs;